/**
 * @file api-legal.ts
 * @description API-клиент юридических документов магазина (phase10.5.X;
 * phase19.16 — виды «Текст / PDF / Группа», редакции, файлы, состав групп,
 * SB139–SB144; контракт — db-design-legal-docs §4 и `@chaiberi/shared` legal).
 *
 * Эндпоинты под `/admin/shop/legal-documents/*`, право SHOP_MANAGE_SETTINGS
 * (SB144). `apiFetch` разворачивает `{ data }` и бросает `ApiError` с кодом —
 * текст для человека даёт `legalErrorText` (lib/legal/legal-errors.ts).
 * Ответы списка и карточки читаются терпимо (`legal-normalize.ts`): до
 * выкладки нового API приходит прежняя форма.
 *
 * Контракт по факту api (2026-10-19, уточнён по ревью): отдельного адреса
 * «Показать снова» нет — это `POST /:id/publish` снятого документа без
 * правок (сервер сам показывает ту же редакцию); `publish` и `discard` — с
 * `expectedVersion`; группа
 * сертификатов — `POST /certificates-group` (сразу опубликована);
 * `DELETE /:id` → 204; адрес занят — `legal_slug_taken`.
 */

import {
  type LegalDocumentCreateRequest,
  type LegalDocumentDto,
  type LegalDocumentListItemDto,
  type LegalDocumentListQuery,
  type LegalDocumentUpdateRequest,
  type LegalPublishResultDto,
  type LegalRevisionDto,
  type LegalRevisionsPageDto,
  type LegalUnpublishResultDto,
} from "@chaiberi/shared";

import { apiDownload, apiFetch, type ApiFileResponse } from "./api-client";
import { normalizeLegalDocument, normalizeLegalListItem } from "./legal/legal-normalize";
import { invalidateLegalTargets } from "./site-view/legal-targets-store";
import { invalidateLiveSiteIssues } from "./site-view/live-issues-store";
import { uploadWithProgress, type UploadWithProgressOptions } from "./upload-with-progress";

export type {
  LegalDocumentDto,
  LegalDocumentListItemDto,
  LegalDocumentListQuery,
  LegalPublishResultDto,
  LegalRevisionDto,
  LegalRevisionsPageDto,
  LegalUnpublishResultDto,
};

const BASE = "/admin/shop/legal-documents";

/**
 * Правовые документы — проверка сайта SB125: плашка «Сайт работает без…»
 * перечитается; общий список целей ссылок (меню, подвал) — тоже.
 */
function afterDocChange<T>(result: T): T {
  invalidateLiveSiteIssues();
  invalidateLegalTargets();
  return result;
}

/** Ответ списка: `items` (прежний API добавлял `total`). */
export interface LegalDocumentListResult {
  items: LegalDocumentListItemDto[];
}

/**
 * Ответ публикации и снятия: `{ document, revision, warnings }`. Пришла
 * голая карточка (форма ответа unpublish в контракте не описана) — читаем её
 * как документ без новой редакции и предупреждений.
 */
function toActionResult(raw: object | null): LegalPublishResultDto {
  if (raw && "document" in raw) {
    const r = raw as Partial<LegalPublishResultDto> & { document: object };
    return { document: normalizeLegalDocument(r.document), revision: r.revision ?? null, warnings: r.warnings ?? [] };
  }
  return { document: normalizeLegalDocument(raw ?? {}), revision: null, warnings: [] };
}

// ─── Список и карточка ─────────────────────────────────────────────────────

export async function listLegalDocs(query: LegalDocumentListQuery = {}): Promise<LegalDocumentListResult> {
  const params = new URLSearchParams();
  if (query.status && query.status !== "all") params.set("status", query.status);
  if (query.kind) params.set("kind", query.kind);
  if (query.q?.trim()) params.set("q", query.q.trim());
  const qs = params.toString();
  const res = await apiFetch<{ items?: object[] } | null>(`${BASE}${qs ? `?${qs}` : ""}`);
  return { items: (res?.items ?? []).map(normalizeLegalListItem) };
}

export async function getLegalDoc(id: string): Promise<LegalDocumentDto> {
  return normalizeLegalDocument(await apiFetch<object>(`${BASE}/${id}`));
}

export async function createLegalDoc(payload: LegalDocumentCreateRequest): Promise<LegalDocumentDto> {
  const doc = await apiFetch<object>(BASE, { method: "POST", json: payload });
  return afterDocChange(normalizeLegalDocument(doc));
}

/**
 * Группа «Сертификаты и декларации» (SB141): свой адрес API, группа
 * создаётся сразу опубликованной, одна на магазин (повтор — 422
 * `legal_certificates_group_exists`). Адрес и название — умолчания сервера.
 */
export async function createCertificatesGroup(): Promise<LegalDocumentDto> {
  // Пустое JSON-тело: без него API отвечал 500 (QA 2026-10-20).
  const doc = await apiFetch<object>(`${BASE}/certificates-group`, { method: "POST", json: {} });
  return afterDocChange(normalizeLegalDocument(doc));
}

/** Черновик (у группы — сразу на сайте); `expectedVersion` — защита от правок в другом окне. */
export async function updateLegalDoc(id: string, payload: LegalDocumentUpdateRequest): Promise<LegalDocumentDto> {
  const doc = await apiFetch<object>(`${BASE}/${id}`, { method: "PATCH", json: payload });
  return afterDocChange(normalizeLegalDocument(doc));
}

export function removeLegalDoc(id: string): Promise<void> {
  return apiFetch<void>(`${BASE}/${id}`, { method: "DELETE" }).then(afterDocChange);
}

// ─── Публикация ────────────────────────────────────────────────────────────

/** Опубликовать черновик новой редакцией; у снятого без правок — показать ту же редакцию снова. */
export async function publishLegalDoc(id: string, expectedVersion: number): Promise<LegalPublishResultDto> {
  const res = await apiFetch<object | null>(`${BASE}/${id}/publish`, { method: "POST", json: { expectedVersion } });
  return afterDocChange(toActionResult(res));
}

export async function unpublishLegalDoc(id: string): Promise<LegalUnpublishResultDto> {
  const res = await apiFetch<object | null>(`${BASE}/${id}/unpublish`, { method: "POST" });
  const { document, warnings } = toActionResult(res);
  return afterDocChange({ document, warnings });
}

/** «Отменить правки»: черновик := действующая редакция. */
export async function discardLegalDraft(id: string, expectedVersion: number): Promise<LegalDocumentDto> {
  const doc = await apiFetch<object>(`${BASE}/${id}/discard`, { method: "POST", json: { expectedVersion } });
  return afterDocChange(normalizeLegalDocument(doc));
}

// ─── Файл PDF ──────────────────────────────────────────────────────────────

/** Загрузить файл черновика (multipart `file`) с процентом и отменой. */
export async function uploadLegalFile(
  id: string,
  file: File,
  options: Pick<UploadWithProgressOptions, "onProgress" | "signal"> = {},
): Promise<LegalDocumentDto> {
  const form = new FormData();
  form.append("file", file);
  const doc = await uploadWithProgress<object>(`${BASE}/${id}/file`, form, {
    ...options,
    // Прокси может ответить на большой файл своей страницей без JSON.
    fallbackCodes: { 413: "legal_file_too_large", 502: "storage_unavailable" },
  });
  return afterDocChange(normalizeLegalDocument(doc));
}

/** Убрать новый файл черновика — вернуться к файлу действующей редакции (`expectedVersion` — защита от чужих правок). */
export async function deleteLegalDraftFile(id: string, expectedVersion?: number): Promise<LegalDocumentDto> {
  const qs = expectedVersion === undefined ? "" : `?expectedVersion=${expectedVersion}`;
  return afterDocChange(normalizeLegalDocument(await apiFetch<object>(`${BASE}/${id}/file${qs}`, { method: "DELETE" })));
}

/** Файл для просмотра: `draft` — файл черновика, число — файл редакции. */
export function downloadLegalFile(id: string, source: "draft" | number): Promise<ApiFileResponse> {
  return apiDownload(`${BASE}/${id}/file?source=${source}`, {}, "document.pdf");
}

// ─── Редакции ──────────────────────────────────────────────────────────────

/** История редакций постранично: `before` — `nextBefore` прошлой страницы. */
export async function listLegalRevisions(id: string, before?: number | null): Promise<LegalRevisionsPageDto> {
  const qs = before ? `?before=${before}` : "";
  const res = await apiFetch<LegalRevisionsPageDto | null>(`${BASE}/${id}/revisions${qs}`);
  return { items: res?.items ?? [], nextBefore: res?.nextBefore ?? null };
}

export function getLegalRevision(id: string, number: number): Promise<LegalRevisionDto> {
  return apiFetch<LegalRevisionDto>(`${BASE}/${id}/revisions/${number}`);
}

// ─── Состав группы ─────────────────────────────────────────────────────────

/** Весь упорядоченный состав группы; `move` — забрать документ из другой группы. */
export async function setLegalGroupMembers(groupId: string, documentIds: string[], move = false): Promise<LegalDocumentDto> {
  const doc = await apiFetch<object>(`${BASE}/${groupId}/members`, {
    method: "PUT",
    json: move ? { documentIds, move } : { documentIds },
  });
  return afterDocChange(normalizeLegalDocument(doc));
}

/** Убрать документ из группы: тот же PUT без него (сам документ не удаляется). */
export function removeLegalGroupMember(group: LegalDocumentDto, documentId: string): Promise<LegalDocumentDto> {
  const ids = (group.members ?? []).map((m) => m.id).filter((memberId) => memberId !== documentId);
  return setLegalGroupMembers(group.id, ids);
}
